import React, { useState, useEffect, useRef, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Printer, Download, TrendingUp, Heart, Calendar } from "lucide-react";
import { formatCurrency, COMMITTEE } from "../utils/mockData";
import { eventsApi } from "../utils/api";

function PrintableReport({ donations, expenses, reportRef }) {
  const totalDonations = donations.reduce((s, d) => s + Number(d.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div ref={reportRef} className="p-8 font-sans text-gray-800">
      <style>{`@media print { body { -webkit-print-color-adjust: exact; } }`}</style>
      <div className="text-center mb-8 pb-6 border-b-2 border-orange-500">
        <div className="text-4xl mb-2">🕉️</div>
        <h1 className="text-xl font-black text-gray-900">{COMMITTEE.name}</h1>
        <h2 className="text-base font-bold text-orange-600 mt-1">GANESH CHATURTHI 2026 — FESTIVAL REPORT</h2>
        <p className="text-xs text-gray-500 mt-1">Sep 14 – Sep 23, 2026 | {COMMITTEE.address}</p>
        <p className="text-xs text-gray-400 mt-0.5">Generated on {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</p>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Donations", value: formatCurrency(totalDonations), color: "text-green-700" },
          { label: "Total Expenses", value: formatCurrency(totalExpenses), color: "text-red-700" },
          { label: "Net Balance", value: formatCurrency(totalDonations - totalExpenses), color: "text-blue-700" },
          { label: "Total Donors", value: donations.length, color: "text-orange-700" },
        ].map((s) => (
          <div key={s.label} className="border border-gray-200 rounded-xl p-3 text-center">
            <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-[11px] text-gray-500 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-bold text-gray-800 border-b border-gray-200 pb-2 mb-3">All Donations</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-2 font-semibold text-gray-600">Receipt No</th>
              <th className="text-left p-2 font-semibold text-gray-600">Donor Name</th>
              <th className="text-left p-2 font-semibold text-gray-600">Purpose</th>
              <th className="text-left p-2 font-semibold text-gray-600">Mode</th>
              <th className="text-right p-2 font-semibold text-gray-600">Amount</th>
            </tr>
          </thead>
          <tbody>
            {[...donations].sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0)).map((d) => (
              <tr key={d.id} className="border-b border-gray-100">
                <td className="p-2 font-mono text-orange-600">{d.receiptNo}</td>
                <td className="p-2 font-semibold">{d.donorName}</td>
                <td className="p-2 text-gray-500">{d.purpose}</td>
                <td className="p-2 text-gray-500">{d.paymentMode}</td>
                <td className="p-2 text-right font-bold text-green-700">{formatCurrency(Number(d.amount || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50">
              <td colSpan={4} className="p-2 font-bold text-gray-800 text-right">Total:</td>
              <td className="p-2 text-right font-black text-green-800">{formatCurrency(totalDonations)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-bold text-gray-800 border-b border-gray-200 pb-2 mb-3">Expense Summary</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-2 font-semibold text-gray-600">Category</th>
              <th className="text-left p-2 font-semibold text-gray-600">Description</th>
              <th className="text-left p-2 font-semibold text-gray-600">Vendor</th>
              <th className="text-right p-2 font-semibold text-gray-600">Amount</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} className="border-b border-gray-100">
                <td className="p-2 font-semibold text-gray-700">{e.category}</td>
                <td className="p-2 text-gray-500">{e.description}</td>
                <td className="p-2 text-gray-500">{e.vendor}</td>
                <td className="p-2 text-right font-bold text-red-700">{formatCurrency(Number(e.amount || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50">
              <td colSpan={3} className="p-2 font-bold text-gray-800 text-right">Total:</td>
              <td className="p-2 text-right font-black text-red-800">{formatCurrency(totalExpenses)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-8 pt-4 border-t border-gray-300 flex justify-between text-xs text-gray-500">
        <span>Prepared by: {COMMITTEE.name}</span>
        <span>utsav.btrix.in | {COMMITTEE.email}</span>
      </div>
    </div>
  );
}

function downloadCSV(filename, rows) {
  const content = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function Reports({ donations = [], expenses = [] }) {
  const [eventCount, setEventCount] = useState(0);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  useEffect(() => {
    eventsApi.list().then((res) => setEventCount((res.items || []).length)).catch(console.error);
  }, []);

  const totalDonations = donations.reduce((s, d) => s + Number(d.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const chartData = useMemo(() => {
    const byDate = {};
    donations.forEach((d) => {
      const date = d.date || "";
      if (!date) return;
      if (!byDate[date]) byDate[date] = { amount: 0, count: 0 };
      byDate[date].amount += Number(d.amount || 0);
      byDate[date].count += 1;
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, data]) => ({
        day: new Date(date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        amount: data.amount,
        count: data.count,
      }));
  }, [donations]);

  const exportDonations = () => {
    const headers = ["Receipt No", "Donor Name", "Phone", "Address", "Amount", "Payment Mode", "Purpose", "Date"];
    const rows = donations.map((d) => [
      d.receiptNo || "", d.donorName || "", d.phone || "", d.address || "",
      Number(d.amount || 0), d.paymentMode || "", d.purpose || "", d.date || "",
    ]);
    downloadCSV("donations.csv", [headers, ...rows]);
  };

  const exportExpenses = () => {
    const headers = ["Category", "Description", "Vendor", "Bill No", "Amount", "Date"];
    const rows = expenses.map((e) => [
      e.category || "", e.description || "", e.vendor || "", e.billNo || "",
      Number(e.amount || 0), e.date || "",
    ]);
    downloadCSV("expenses.csv", [headers, ...rows]);
  };

  const reportCards = [
    {
      title: "Donation Report",
      desc: "Complete donor list with receipt details",
      icon: Heart,
      color: "bg-green-50 border-green-200 text-green-700",
      count: `${donations.length} records`,
      onExport: exportDonations,
    },
    {
      title: "Expense Report",
      desc: "Itemized expense breakdown by category",
      icon: TrendingUp,
      color: "bg-red-50 border-red-200 text-red-700",
      count: `${expenses.length} entries`,
      onExport: exportExpenses,
    },
    {
      title: "Event Summary",
      desc: "All events scheduled for the festival",
      icon: Calendar,
      color: "bg-blue-50 border-blue-200 text-blue-700",
      count: `${eventCount} events`,
      onExport: null,
    },
  ];

  return (
    <div className="p-4 lg:p-6 space-y-5 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500">Generate and export festival reports</p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl text-sm shadow-lg shadow-orange-500/20">
          <Printer size={16} /> Print Full Report
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {reportCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className={`border rounded-2xl p-5 ${card.color}`}>
              <div className="flex items-start justify-between mb-3">
                <Icon size={20} />
                <span className="text-xs font-semibold">{card.count}</span>
              </div>
              <h3 className="text-sm font-bold mb-1">{card.title}</h3>
              <p className="text-xs opacity-70">{card.desc}</p>
              {card.onExport && (
                <button onClick={card.onExport}
                  className="mt-3 flex items-center gap-1.5 text-xs font-semibold opacity-80 hover:opacity-100 transition-opacity">
                  <Download size={12} /> Export CSV
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* P&L Summary */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Financial Summary</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-sm font-semibold text-gray-700">Total Income (Donations)</span>
            </div>
            <span className="text-base font-black text-green-700">{formatCurrency(totalDonations)}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-red-50 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-sm font-semibold text-gray-700">Total Expenditure</span>
            </div>
            <span className="text-base font-black text-red-700">({formatCurrency(totalExpenses)})</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl border-2 border-blue-200">
            <span className="text-sm font-bold text-gray-800">Net Balance</span>
            <span className="text-xl font-black text-blue-700">{formatCurrency(totalDonations - totalExpenses)}</span>
          </div>
        </div>
      </div>

      {/* Daily Collections Chart */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Collection by Date</h2>
        {chartData.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-8">No donation data yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="amount" name="Amount" radius={[4, 4, 0, 0]} fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Hidden print content */}
      <div className="hidden">
        <PrintableReport donations={donations} expenses={expenses} reportRef={printRef} />
      </div>
    </div>
  );
}
